import { DownloadOutlined, DownOutlined, SearchOutlined, UpOutlined } from '@ant-design/icons';
import { Button, Checkbox, DatePicker, Popover, Select, Space, Spin, TreeSelect } from 'antd';
import { Tabs } from 'antd/lib';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import Layout from '../../Layout';
import { getListGroup } from '../../api/groupCustomerV2/groupCustomerV2Api';
import { exportCustomerReportExcel, exportExcelVehicleOwnershipReportByBrand, getCustomerByInformationChannel, getDataCustomerReportAddress, getDataCustomerReportAgeCar, getDataCustomerReportCarOwnedBrand, getDataCustomerReportCarOwnedBrandByUnit, getDataCustomerReportCarOwnerUsed, getDataCustomerReportGenderBussiness, getDataCustomerReportJob, getListVehicleOwnershipReportByBrand, getVehicleOwnershipReport, getVehicleOwnershipReportByBrand, IReportAge, IReportBranchOwnership, IVehicleOwnershipReport } from '../../api/reportCustomer/reportCustomerApi';
import { getCLProfession, getListCarCompany, getListProvince, getSLUnitByPermission, ISelectOption } from '../../api/selectOptions/selectOptionsApi';
import { convertGroupValueToTree, convertNationwideValueToTree_V2 } from '../../api/unit/unitApi';
import BreadcrumbBlock from '../../components/BreadcrumbBlock/BreadcrumbBlock';
import { LIMIT_EXPORT_EXCEL, NO_DATA } from '../../env';
import useDebounce from '../../hook/useDebounce';
import useLoading from '../../hook/useLoading';
import { useMultiInViewport } from '../../hook/useMultiInViewport';
import useThrottle from '../../hook/useThrottle';
import useToast from '../../hook/useToast';
import { dataGender, dataTypeCustomer } from '../../services/data';
import { selectorInfoUser } from '../../store/slices/infoUserSlice';
import { fommatSelectOption, formatPrice, formatPriceV2, getPermissionClass, getUserPermissions } from '../../until';
import { useInViewport } from './Component/useInViewport';
import { AgeDistributionChart } from './ComponentChart/AgeDistributionChart';
import { ChartCard } from './ComponentChart/ChartCard';
import { CustomerTypePie } from './ComponentChart/CustomerTypePie';
import { GenderDonut } from './ComponentChart/GenderDonut';
import { HorizontalBarChart } from './ComponentChart/HorizontalBarChart';
import VehicleOwnershipChart from './ComponentChart/VehicleOwnershipChart';
import './Customers.scss';
import ReportMarketingCampaign from './ReportMarketingCampaign';
import TooltipComponent from '../../components/TooltipComponent';
import { selectorPermission } from '../../store/slices/permissionSlice';

const { RangePicker } = DatePicker;


// ─── Mock data ───────────────────────────────────────────────────────────────

const mockChartData = [
  { brand: 'KIA', own1: 150, own2: 80, own3: 150 },
  { brand: 'MAZDA', own1: 120, own2: 100, own3: 160 },
  { brand: 'BMW', own1: 100, own2: 60, own3: 80 },
  { brand: 'TẢI BUS', own1: 60, own2: 30, own3: 70 },
  { brand: 'PEUGEOT MTC', own1: 50, own2: 35, own3: 65 },
  { brand: 'MINI', own1: 45, own2: 30, own3: 60 },
  { brand: 'BUS & MINI BUS', own1: 40, own2: 30, own3: 70 },
  { brand: 'PEUGEOT', own1: 50, own2: 35, own3: 60 },
  { brand: 'MOTORRAD', own1: 50, own2: 30, own3: 60 },
  { brand: 'SR Bình Triệu', own1: 45, own2: 25, own3: 65 },
];

interface CampaignRow {
  id: number;
  brand: string;
  campaignName: string;
  customerGroup: string;
  sent: number;
  success: number;
  viewed: number;
  ctr: string;
  leftInfo: string;
  sentTime: string;
}

const mockTableData: CampaignRow[] = [
  { id: 1, brand: 'Tiếp thị', campaignName: 'Giới thiệu xe BMW', customerGroup: 'Mới, BMW', sent: 200, success: 190, viewed: 20, ctr: '10%', leftInfo: '10%', sentTime: '28/10/2023' },
  { id: 2, brand: 'Tiếp thị', campaignName: 'Giới thiệu xe BMW', customerGroup: 'Tất cả', sent: 200, success: 190, viewed: 20, ctr: '10%', leftInfo: '10%', sentTime: '28/10/2023' },
  { id: 3, brand: 'Tiếp thị', campaignName: 'Đăng ký lái thử', customerGroup: 'Mới, BMW', sent: 200, success: 190, viewed: 20, ctr: '10%', leftInfo: '10%', sentTime: '28/10/2023' },
  { id: 4, brand: 'Tiếp thị', campaignName: 'Đăng ký lái thử', customerGroup: 'Tất cả', sent: 200, success: 200, viewed: 20, ctr: '10%', leftInfo: '10%', sentTime: '28/10/2023' },
  { id: 5, brand: 'Tiếp thị', campaignName: 'Đăng ký lái thử', customerGroup: 'Mới, BMW', sent: 200, success: 200, viewed: 20, ctr: '10%', leftInfo: '10%', sentTime: '28/10/2023' },
];

// ─── Chart colors ─────────────────────────────────────────────────────────────

const CHART_COLORS = {
  own1: '#8979FF',
  own2: '#FF928A',
  own3: '#3CC3DF',
  own4: '#FFAE4C',
  own5: '#537FF1',
  own6_10: '#6FD195',
  own10plus: '#8C63DA',
};

// Dữ liệu mẫu theo đúng thứ tự và phân bố như ảnh
const rawData = [
  {
    brand: "KIA",
    own1: 320,    // 1 xe
    own2: 145,    // 2 xe
    own3: 65,     // 3 xe
    own4: 28,     // 4 xe
    own5: 12,     // 5 xe
    own6_10: 8,   // 6 - 10 xe
    own10plus: 4, // > 10 xe
    total: 582,   // tổng ~ gần 600
  },
  {
    brand: "MAZDA",
    own1: 260,
    own2: 110,
    own3: 48,
    own4: 20,
    own5: 9,
    own6_10: 6,
    own10plus: 3,
    total: 456,   // ~ 450
  },
  {
    brand: "BMW",
    own1: 210,
    own2: 85,
    own3: 40,
    own4: 18,
    own5: 8,
    own6_10: 7,
    own10plus: 5,
    total: 373,   // ~ 375
  },
  {
    brand: "TAI BUS",
    own1: 140,
    own2: 55,
    own3: 22,
    own4: 10,
    own5: 5,
    own6_10: 3,
    own10plus: 1,
    total: 236,
  },
  {
    brand: "PEUGEOT MTC",
    own1: 110,
    own2: 45,
    own3: 18,
    own4: 8,
    own5: 4,
    own6_10: 2,
    own10plus: 1,
    total: 188,
  },
  {
    brand: "MINI",
    own1: 85,
    own2: 32,
    own3: 14,
    own4: 6,
    own5: 3,
    own6_10: 2,
    own10plus: 1,
    total: 143,
  },
  {
    brand: "BUS & MINI BUS",
    own1: 60,
    own2: 22,
    own3: 10,
    own4: 4,
    own5: 2,
    own6_10: 1,
    own10plus: 1,
    total: 100,
  },
  {
    brand: "PEUGEOT",
    own1: 45,
    own2: 18,
    own3: 8,
    own4: 3,
    own5: 2,
    own6_10: 1,
    own10plus: 0,
    total: 77,
  },
  {
    brand: "MOTORRAD",
    own1: 30,
    own2: 12,
    own3: 5,
    own4: 2,
    own5: 1,
    own6_10: 1,
    own10plus: 0,
    total: 51,
  },
  {
    brand: "TCXAM",
    own1: 18,
    own2: 7,
    own3: 3,
    own4: 1,
    own5: 1,
    own6_10: 0,
    own10plus: 0,
    total: 30,
  },
];

export enum TYPEFILTERTOP {
  JOB = 0,
  REGION = 1,
  BRAND = 2,
  BRANDSANDDEALERSHIPS = 3,
  CUSTOMERJOBS = 4,
  VEHICLEOWNERSHIPREPORTBYBRAND = 5,
  CUSTOMERBYINFORMATIONCHANNEL = 6
}



export const dataSelectTopReport = [
  { label: "Top 15", value: 15 },
  { label: "Top 10", value: 10 },
  { label: "Top 5", value: 5 }
]

export const dataSelectAge = [
  { label: "Không xác định", value: 0 },
  { label: "0 - 18", value: 1 },
  { label: "18 - 25", value: 2 },
  { label: "26 - 35", value: 3 },
  { label: "36 - 45", value: 4 },
  { label: "46 - 55", value: 5 },
  { label: "56 +", value: 6 },
]

const sections = [
  "customerReportGenderBussiness",
  "customerReportAgeCar",
  "customerReportJob",
  "customerReportAddress",
  "customerReportCarOwnedBrand",
  "customerReportCarOwnerUsed",
  "customerReportCarOwnedBrandByUnit",
  "customerJobs",
  "customerByInformationChannel",
  "vehicleOwnershipReportByBrand",
  "vehicleOwnershipReportByBrandTable",
  "vehicleOwnershipReport"
] as const;


type VehicleOwnershipRow = {
  dealer: string;
  [brand: string]: string | number | undefined;
};

export const buildTopDealerWithOther = (
  data: VehicleOwnershipRow[],
  top: number
): VehicleOwnershipRow[] => {
  if (!data?.length || top <= 1) return [];

  // 1. Sort theo tổng dealer
  const sorted = [...data]
    .map(row => ({
      ...row,
      __total: calcDealerTotal(row)
    }))
    .sort((a, b) => (b.__total as number) - (a.__total as number));

  // 2. Lấy top (N - 1)
  const mainDealers = sorted.slice(0, top - 1).map(({ __total, ...r }) => r);

  // 3. Dealer còn lại gộp thành "Khác"
  const otherDealers = sorted.slice(top - 1).map(({ __total, ...r }) => r);

  if (otherDealers.length === 0) {
    return mainDealers;
  }

  const otherDealer = mergeDealersToOther(otherDealers);

  return [...mainDealers, otherDealer];
};


const mergeDealersToOther = (
  dealers: VehicleOwnershipRow[],
  dealerName = "Khác"
): VehicleOwnershipRow => {
  const result: VehicleOwnershipRow = { dealer: dealerName };

  dealers.forEach(row => {
    Object.entries(row).forEach(([key, value]) => {
      if (key === "dealer") return;
      if (typeof value !== "number") return;

      result[key] = (result[key] as number || 0) + value;
    });
  });

  return result;
};
const calcDealerTotal = (row: VehicleOwnershipRow): number =>
  Object.entries(row).reduce((sum, [key, value]) => {
    if (key === "dealer") return sum;
    if (typeof value !== "number") return sum;
    return sum + value;
  }, 0);

const buildTopWithOther = (
  data: { name: string; value: number }[],
  top: number
) => {
  const sorted = [...data].sort((a, b) => b.value - a.value);
  const main = sorted.slice(0, top - 1);
  const otherSum = sorted.slice(top - 1).reduce((s, i) => s + i.value, 0);

  return otherSum > 0 ? [...main, { name: "Khác", value: otherSum, id: -1 }] : main;
};
export const formatVehicleOwnershipChart = (
  data: IReportBranchOwnership
) => {
  if (!data) return []

  return Object.entries(data).map(([dealer, brands]) => ({
    dealer,
    ...brands
  }))
}
const buildTopWithOtherV2 = (
  data: { id: number; name: string; value: number }[] | undefined,
  top: number
): { id: number[]; name: string; value: number }[] => {

  if (!data?.length) return [];

  const sorted = [...data].sort(
    (a, b) => b.value - a.value
  );

  // top = 15 => lấy toàn bộ, không gom "Khác"
  if (top === 15) {
    return sorted.map(item => ({
      ...item,
      id: [item.id],
    }));
  }

  // Các item chính
  const main = sorted
    .slice(0, top - 1)
    .map(item => ({
      ...item,
      id: [item.id],
    }));

  // Các item còn lại gom vào "Khác"
  const others = sorted.slice(top - 1);

  const otherSum = others.reduce(
    (sum, item) => sum + item.value,
    0
  );

  const otherIds = others.map(
    item => item.id
  );

  return otherSum > 0
    ? [
      ...main,
      {
        name: "Khác",
        value: otherSum,
        id: otherIds,
      },
    ]
    : main;
};



// data.ts
export const initGenderData = [
  { name: "Nam", value: 0, color: "#1890FF" },
  { name: "Nữ", value: 0, color: "#4F00B2" },
  { name: "Không xác định", value: 0, color: "#F55E09" },
];

export const initCustomerTypeData = [
  { name: "Cá nhân", value: 0, color: "#3DA5F4" },
  { name: "Đơn vị, tổ chức", value: 0, color: "#FF8A00" },
];

export const initAgeDistributionData = [
  { age: "0-18", owned: 0, used: 0, interested: 0 },
  { age: "18-25", owned: 0, used: 0, interested: 0 },
  { age: "26-35", owned: 0, used: 0, interested: 0 },
  { age: "36-45", owned: 0, used: 0, interested: 0 },
  { age: "46-55", owned: 0, used: 0, interested: 0 },
  { age: "56+", owned: 0, used: 0, interested: 0 },
];

export const topByJobs = [
  { name: "Kinh doanh dịch vụ vận tải hành khách", value: 0 },
  { name: "Kinh doanh vận tải hàng hóa", value: 0 },
  { name: "Dịch vụ Taxi, Công nghệ gọi xe", value: 0 },
  { name: "Dịch vụ du lịch, giải trí sự kiện", value: 0 },
  { name: "Sản xuất - Kinh doanh vật tư, VLXD", value: 0 },
  { name: "Trung tâm đào tạo & sát hạch lái xe", value: 0 },
  { name: "Kinh doanh dịch vụ cho thuê xe", value: 0 },
  { name: "Giáo viên, giảng viên, trung tâm đào tạo", value: 0 },
  { name: "Phóng viên, biên tập viên", value: 0 },
  { name: "Khác", value: 0 },
];

export const topByRegion = [
  { name: "Thành phố Hồ Chí Minh", value: 0 },
  { name: "Thành phố Hà Nội", value: 0 },
  { name: "Tỉnh Đà Nẵng", value: 0 },
  { name: "Thành phố Cần Thơ", value: 0 },
  { name: "Tỉnh Bình Dương", value: 0 },
  { name: "Tỉnh Bến Tre", value: 0 },
  { name: "Tỉnh Gia Lai", value: 0 },
  { name: "Thành phố Hải Phòng", value: 0 },
  { name: "Tỉnh Nghệ An", value: 0 },
  { name: "Khác", value: 0 },
];


export const initDataOwnedBrand = [
  { name: "PEUGEOT MTC", value: 0 },
  { name: "MOTORRAD", value: 0 },
  { name: "MAZDA", value: 0 },
  { name: "MINI", value: 0 },
  { name: "Khác", value: 0 },
];

export const initOwnershipData = [
  { name: "Xe sở hữu", value: 0, color: "#FF7A00" },
  { name: "Xe sử dụng", value: 0, color: "#6F42E5" },
  { name: "Xe quan tâm", value: 0, color: "#2BB7DC" },
];

export interface IDataDefaulFilter {
  isMore: boolean
  unitId?: number[];
  businessCheck?: number[]
  gender?: number[],
  job?: number[],
  province?: number[],
  carBrand?: number[],
  age?: number[],
  group?: number[]
}

interface CustomerFilterSearch {
  searchGroup?: string
  searchJob?: string
  searchProvince?: string
}

export const dataDefaulFilter: IDataDefaulFilter = {
  isMore: false,
};

const ReportCustomer: React.FC = () => {

  const pushToast = useToast()
  const pushLoading = useLoading()
  const { unitTemp } = useSelector(selectorInfoUser)

  const location = useLocation()

  const permissionSlice = useSelector(selectorPermission)
  const { add, edit, delete: isDelete, download } = getUserPermissions
  (permissionSlice, location.pathname)

  const {
    ref: genderRef,
    isVisible: genderVisible
  } = useInViewport<HTMLDivElement>();


  const {
    ref: CarOwnedBrandByUnitRef,
    isVisible: CarOwnedBrandByUnitVisible
  } = useInViewport<HTMLDivElement>();

  const { visibleMap, loadingMap, loadedMap, setRef, setLoading, setLoaded, resetAllLoading, resetAllLoaded } = useMultiInViewport(sections, {
    threshold: 0.0,
  });


  const [sLUnitByPermission, setSLUnitByPermission] = useState<any>()
  const [sLProfession, setSLProfession] = useState<ISelectOption[]>([])
  const [sLProvice, setSLProvice] = useState<ISelectOption[]>([])
  const [sLCategoryCar, setSLCategoryCar] = useState<ISelectOption[]>([])
  const [listGroup, setListGroup] = useState<any[]>([])

  const [valueTop, setValueTop] = useState<Record<TYPEFILTERTOP, number>>({
    [TYPEFILTERTOP.JOB]: 10,
    [TYPEFILTERTOP.REGION]: 10,
    [TYPEFILTERTOP.BRAND]: 10,
    [TYPEFILTERTOP.BRANDSANDDEALERSHIPS]: 10,
    [TYPEFILTERTOP.CUSTOMERJOBS]: 10,
    [TYPEFILTERTOP.VEHICLEOWNERSHIPREPORTBYBRAND]: 10,
    [TYPEFILTERTOP.CUSTOMERBYINFORMATIONCHANNEL]: 10
  });




  const [dataFilter, setDataFilter] = useState<IDataDefaulFilter>(dataDefaulFilter)
  const [dataFilterTmp, setDataFilterTmp] = useState<IDataDefaulFilter>(dataDefaulFilter)
  const [searchFilter, setSearchFilter] = useState<CustomerFilterSearch>()


  const [dataCustomerReportGender, setDataCustomerReportGender] = useState(initGenderData)
  const [dataCustomerType, setDataCustomerType] = useState(initCustomerTypeData)
  const [dataAgeDistribution, setDataAgeDistribution] = useState(initAgeDistributionData)
  //
  const [dataCustomerJobs, setDataCustomerJobs] = useState<any[]>()
  const [dataCustomerAddress, setDataCustomerAddress] = useState<any[]>()
  const [dataOwnedBrand, setDataOwnedBrand] = useState(initDataOwnedBrand)
  const [dataOwnership, setDataOwnership] = useState(initOwnershipData)
  const [dataCarOwnedBrandByUnit, setDataCarOwnedBrandByUnit] = useState<any>([])
  const [brandsCarOwnedBrandByUnit, setBrandsCarOwnedBrandByUnit] = useState<any>([])
  const [dataCustomerByInformationChannel, setDataCustomerByInformationChannel] = useState<any[]>([])
  const [dataVehicleOwnershipReportByBrand, setDataVehicleOwnershipReportByBrand] = useState<any>([])
  const [dataVehicleOwnershipReportByBrandTable, setDataVehicleOwnershipReportByBrandTable] = useState<any>([])

  const [dataVehicleOwnershipReport, setDataVehicleOwnershipReport] = useState<IVehicleOwnershipReport>()
  const [activeTab, setActiveTab] = useState<string>('1')

  const dataFormatCustomerJobs = useMemo(() => buildTopWithOtherV2(dataCustomerJobs, valueTop[TYPEFILTERTOP.JOB])
    , [dataCustomerJobs, valueTop[TYPEFILTERTOP.JOB]])

  const dataFormatCustomerAddress = useMemo(() => buildTopWithOtherV2(dataCustomerAddress, valueTop[TYPEFILTERTOP.REGION])
    , [dataCustomerAddress, valueTop[TYPEFILTERTOP.REGION]])

  const dataFormatTopOwnedBrand = useMemo(() => buildTopWithOther(dataOwnedBrand, valueTop[TYPEFILTERTOP.BRAND])
    , [dataOwnedBrand, valueTop[TYPEFILTERTOP.BRAND]])

  const dataFormatCarOwnedBrandByUnit = useMemo(() => buildTopDealerWithOther(dataCarOwnedBrandByUnit, valueTop[TYPEFILTERTOP.BRANDSANDDEALERSHIPS])
    , [dataCarOwnedBrandByUnit, valueTop[TYPEFILTERTOP.BRANDSANDDEALERSHIPS]])

  const dataFormatCustomerByInformationChannel = useMemo(() => buildTopWithOther(dataCustomerByInformationChannel, valueTop[TYPEFILTERTOP.CUSTOMERBYINFORMATIONCHANNEL])
    , [dataCustomerByInformationChannel, valueTop[TYPEFILTERTOP.CUSTOMERBYINFORMATIONCHANNEL]])


  const dataVehicleOwnershipReportChart = useMemo(() => {
    if (!dataVehicleOwnershipReport) return [];
    const sum = (key: keyof IVehicleOwnershipReport) => dataVehicleOwnershipReport[key] || 0;
    return [
      { name: '1 xe', value: dataVehicleOwnershipReport.one_car },
      { name: '2 xe', value: dataVehicleOwnershipReport.two_car },
      { name: '3 xe', value: dataVehicleOwnershipReport.three_car },
      { name: '4 xe', value: dataVehicleOwnershipReport.four_car },
      { name: '5 xe', value: dataVehicleOwnershipReport.five_car },
      { name: '6 - 10 xe', value: dataVehicleOwnershipReport.six_to_ten_car },
      { name: '> 10 xe', value: dataVehicleOwnershipReport.ten_plus_car },
    ];
  }, [dataVehicleOwnershipReport]);


  const gSLUnitByPermission = async () => {
    const result = await getSLUnitByPermission(unitTemp);
    if (result?.status) {
      setSLUnitByPermission(convertNationwideValueToTree_V2(result.data))
    } else {
    }
  }
  const gSLProfession = useDebounce(async () => {
    const result = await getCLProfession(searchFilter?.searchJob);
    if (result?.status) {
      setSLProfession(fommatSelectOption(result.data))
    } else {
      pushToast(result?.message, "error")
    }
  }, 500)

  const gSLProvice = useDebounce(async () => {
    const result = await getListProvince(searchFilter?.searchProvince);
    if (result?.status) {
      setSLProvice(fommatSelectOption(result.data))
    } else {
      pushToast(result?.message, "error")
    }
  }, 500)
  const gSLCarCompany = async () => {
    const result = await getListCarCompany()
    if (result?.status) {
      setSLCategoryCar(fommatSelectOption(result.data))
    }
  }

  const gSLGroup = useDebounce(async () => {
    const result = await getListGroup([],
      undefined, undefined, undefined, undefined,
      searchFilter?.searchGroup, undefined, undefined, undefined,
      unitTemp);
    if (result?.status) {

      setListGroup(convertGroupValueToTree(result.data))
    }
  }, 500)


  // Biểu đồ tỉ lệ giới tính
  const gDataCustomerReportGenderBussiness = useDebounce(async () => {
    setLoading("customerReportGenderBussiness", true)
    const result = await getDataCustomerReportGenderBussiness(
      unitTemp, dataFilter
    )
    if (result?.status) {
      const { gender, businessCheck } = result.data
      const { totalCustomer, totalElse, totalFemale, totalMale } = gender
      const { totalBusiness, totalCustomer: totalBusinessCheck, totalIndividual } = businessCheck

      setDataCustomerReportGender(prev =>
        prev.map(item => {
          if (item.name === "Nam") return { ...item, value: totalMale, type: 0 };
          if (item.name === "Nữ") return { ...item, value: totalFemale, type: 1 };
          if (item.name === "Không xác định") return { ...item, value: totalElse, type: 2 };
          return item;
        })
      );

      setDataCustomerType(prev =>
        prev.map(item => {
          if (item.name === "Cá nhân") return { ...item, value: totalIndividual, type: 0 };
          if (item.name === "Đơn vị, tổ chức") return { ...item, value: totalBusiness, type: 1 };
          return item;
        })
      );
    } else {
      pushToast(result?.message, "error")
    }

    setLoading("customerReportGenderBussiness", false)
  }, 1000)

  // Biểu đồ  tỉ lệ xe sở hữu và xe sử dụng
  const gDataCustomerReportCarOwnerUsed = useDebounce(async () => {
    setLoading("customerReportCarOwnerUsed", true)
    const resultCarOwnerUsed = await getDataCustomerReportCarOwnerUsed(
      unitTemp, dataFilter
    )
    if (resultCarOwnerUsed?.status) {
      const { invoiceSale, invoiceService, interaction } = resultCarOwnerUsed.data
      setDataOwnership(prev =>
        prev.map(item => {
          if (item.name === "Xe sở hữu") return { ...item, value: invoiceSale };
          if (item.name === "Xe sử dụng") return { ...item, value: invoiceService };
          if (item.name === "Xe quan tâm") return { ...item, value: interaction };
          return item;
        })
      );
    } else {
      pushToast(resultCarOwnerUsed?.message, "error")
    }
    setLoading("customerReportCarOwnerUsed", false)
  }, 1000)

  // xe theo tuổi
  const gDataCustomerReportAgeCar = useDebounce(async () => {
    setLoading("customerReportAgeCar", true)
    const resultCar = await getDataCustomerReportAgeCar(
      unitTemp, dataFilter
    )
    if (resultCar?.status) {
      const mapReportAgeToChartData = (report: IReportAge) => {
        return [
          {
            age: NO_DATA,
            owned: report?.undetermined?.invoiceSale,
            used: report?.undetermined?.invoiceService,
            interested: report?.undetermined?.interaction,
          },
          {
            age: "0-18",
            owned: report.ageUnder18.invoiceSale,
            used: report.ageUnder18.invoiceService,
            interested: report.ageUnder18.interaction,
          },
          {
            age: "18-25",
            owned: report.ageBetween1825.invoiceSale,
            used: report.ageBetween1825.invoiceService,
            interested: report.ageBetween1825.interaction,
          },
          {
            age: "26-35",
            owned: report.ageBetween2635.invoiceSale,
            used: report.ageBetween2635.invoiceService,
            interested: report.ageBetween2635.interaction,
          },
          {
            age: "36-45",
            owned: report.ageBetween3645.invoiceSale,
            used: report.ageBetween3645.invoiceService,
            interested: report.ageBetween3645.interaction,
          },
          {
            age: "46-55",
            owned: report.ageBetween4655.invoiceSale,
            used: report.ageBetween4655.invoiceService,
            interested: report.ageBetween4655.interaction,
          },
          {
            age: "56+",
            owned: report.ageOver55.invoiceSale,
            used: report.ageOver55.invoiceService,
            interested: report.ageOver55.interaction,
          },
        ];
      };
      setDataAgeDistribution(mapReportAgeToChartData(resultCar.data));

    } else {
      pushToast(resultCar?.message, "error")
    }
    setLoading("customerReportAgeCar", false)
  }, 1000)


  // kh theo nghề nghiệp
  const gDataCustomerReportJob = useDebounce(async () => {
    // setLoaded("customerReportJob", false)
    setLoading("customerReportJob", true)
    const resultJobs = await getDataCustomerReportJob(
      unitTemp, dataFilter
    )
    setLoading("customerJobs", true)
    if (resultJobs?.status) {
      const raw = resultJobs.data.map(item => ({
        name: item.profession,
        value: item.total,
        id: item.id
      }));

      setDataCustomerJobs(raw)

    } else {
      pushToast(resultJobs?.message, "error")
    }
    setLoading("customerReportJob", false)
  }, 1000)

  // kh theo tỉnh thành
  const gDataCustomerReportAddress = useDebounce(async () => {
    setLoading("customerReportAddress", true)
    const resultAddress = await getDataCustomerReportAddress(
      unitTemp, dataFilter
    )
    if (resultAddress?.status) {
      const raw = resultAddress.data.map(item => ({
        name: item.province,
        value: item.total,
        id: item.id
      }));
      setDataCustomerAddress(raw)
    } else {
      pushToast(resultAddress?.message, "error")
    }
    setLoading("customerReportAddress", false)
  }, 1000)

  // Chi tiết báo cáo khách hàng theo thương hiệu xe được sở hữu nhiều nhất
  const gDataCustomerReportCarOwnedBrand = useDebounce(async () => {
    setLoading("customerReportCarOwnedBrand", true)
    const resultCarOwnedBrand = await getDataCustomerReportCarOwnedBrand(
      unitTemp, dataFilter
    )
    if (resultCarOwnedBrand?.status) {
      const raw = resultCarOwnedBrand.data.map(item => ({
        name: item.brand,
        value: item.total,
      }));

      setDataOwnedBrand(raw)
    } else {
      pushToast(resultCarOwnedBrand?.message, "error")
    }
    setLoading("customerReportCarOwnedBrand", false)
  }, 1000)



  // Thông tin báo cáo thương hiệu được sở hữu nhiều nhất theo đơn vị bán xe
  const gDataCustomerReportCarOwnedBrandByUnit = useDebounce(async () => {
    setLoading("customerReportCarOwnedBrandByUnit", true)
    const resultCarOwnedBrandByUnit = await getDataCustomerReportCarOwnedBrandByUnit(
      unitTemp, dataFilter
    )
    if (resultCarOwnedBrandByUnit?.status) {
      setDataCarOwnedBrandByUnit(formatVehicleOwnershipChart(resultCarOwnedBrandByUnit.data))
      setBrandsCarOwnedBrandByUnit(resultCarOwnedBrandByUnit.brands)
    } else {
      pushToast(resultCarOwnedBrandByUnit?.message, "error")
    }
    setLoading("customerReportCarOwnedBrandByUnit", false)
  }, 1000)

  // Top 10 khách hàng theo kênh tiếp xúc/ nguồn
  const gCustomerByInformationChannel = useDebounce(async () => {
    setLoaded("customerByInformationChannel", false)
    setLoading("customerByInformationChannel", true)
    const result = await getCustomerByInformationChannel(
      unitTemp, dataFilter
    )

    // const result = await getCustomerByInformationChannel(
    //   valueTop[TYPEFILTERTOP.CUSTOMERBYINFORMATIONCHANNEL], dataFilter
    // )
    if (result?.status) {
      const raw = result.data.map(item => ({
        name: item.informationChannel,
        value: item.totalCustomers,
      }));

      setDataCustomerByInformationChannel(raw)

    } else {
      pushToast(result?.message, "error")
    }

    setLoading("customerByInformationChannel", false)
  }, 1000)


  // Thông tin báo cáo xe sở hữu theo thương hiệu
  const gVehicleOwnershipReport = useDebounce(async () => {

    setLoading("vehicleOwnershipReport", true)
    const result = await getVehicleOwnershipReport(unitTemp, dataFilter)
    if (result?.status) {
      // setDataCarOwnedBrandByUnit(formatVehicleOwnershipChart(resultCarOwnedBrandByUnit.data))

      // const dataTmp = result.data.map((item: any) => ({
      //   name: item.brandName,

      //   own1: item["1car"] ?? 0,
      //   own2: item["2car"] ?? 0,
      //   own3: item["3car"] ?? 0,
      //   own4: item["4car"] ?? 0,
      //   own5: item["5car"] ?? 0,
      //   own6_10: item["6_10car"] ?? 0,
      //   own10plus: item.greaterThan10Car ?? 0,

      //   total: item.total ?? 0,
      // }));
      // setDataVehicleOwnershipReportByBrand(dataTmp)
      setDataVehicleOwnershipReport(result.data)
    } else {
      pushToast(result?.message, "error")
    }
    setLoading("vehicleOwnershipReport", false)
  }, 1000)


  // Thông tin báo cáo kh theo sl xe sở hữu
  const gVehicleOwnershipReportByBrand = useDebounce(async () => {

    setLoaded("vehicleOwnershipReportByBrand", false)
    setLoading("vehicleOwnershipReportByBrand", true)
    const result = await getVehicleOwnershipReportByBrand(
      unitTemp, valueTop[TYPEFILTERTOP.VEHICLEOWNERSHIPREPORTBYBRAND], dataFilter
    )
    if (result?.status) {
      // setDataCarOwnedBrandByUnit(formatVehicleOwnershipChart(resultCarOwnedBrandByUnit.data))

      const dataTmp = result.data.map((item: any) => ({
        name: item.brandName,

        own1: item["1car"] ?? 0,
        own2: item["2car"] ?? 0,
        own3: item["3car"] ?? 0,
        own4: item["4car"] ?? 0,
        own5: item["5car"] ?? 0,
        own6_10: item["6_10car"] ?? 0,
        own10plus: item.greaterThan10Car ?? 0,

        total: item.total ?? 0,
      }));
      setDataVehicleOwnershipReportByBrand(dataTmp)
    } else {
      pushToast(result?.message, "error")
    }
    setLoading("vehicleOwnershipReportByBrand", false)
  }, 1000)



  // Thông tin báo cáo xe sở hữu theo thương hiệu Bảng
  const gListVehicleOwnershipReportByBrand = useDebounce(async () => {

    setLoaded("vehicleOwnershipReportByBrandTable", false)
    setLoading("vehicleOwnershipReportByBrandTable", true)
    const result = await getListVehicleOwnershipReportByBrand(
      unitTemp, LIMIT_EXPORT_EXCEL, dataFilter
    )
    if (result?.status) {
      // setDataCarOwnedBrandByUnit(formatVehicleOwnershipChart(resultCarOwnedBrandByUnit.data))

      const dataTmp = result.data.map((item: any) => ({
        brandId: item.brandId,
        name: item.brandName,

        own1: formatPriceV2(item["1car"] ?? 0),
        own2: formatPriceV2(item["2car"] ?? 0),
        own3: formatPriceV2(item["3car"] ?? 0),
        own4: formatPriceV2(item["4car"] ?? 0),
        own5: formatPriceV2(item["5car"] ?? 0),
        own6_10: formatPriceV2(item["6_10car"] ?? 0),
        own10plus: formatPriceV2(item.greaterThan10Car ?? 0),

        total: formatPriceV2(item.total ?? 0),
      }));
      setDataVehicleOwnershipReportByBrandTable(dataTmp)
    } else {
      pushToast(result?.message, "error")
    }
    setLoading("vehicleOwnershipReportByBrandTable", false)
  }, 1000)


  const handleAgeChange = (value: number, checked: boolean) => {
    setDataFilterTmp(prev => {
      if (!prev) return prev;

      const currentAges = prev.age ?? [];

      const newAges = checked ?
        currentAges.includes(value) ?
          currentAges :
          [...currentAges, value] :
        currentAges.filter(v => v !== value);

      return {
        ...prev,
        age: newAges
      };
    });
  };

  const handleExportReportAll = () => {
    pushLoading(true)
    exportCustomerReportExcel('report/exportCustomerReportExcel', unitTemp, dataFilter).finally(() => {
      pushLoading(false)
    })
  }

  const handleExcelVehicleOwnershipReportByBrand = useThrottle(async () => {
    pushLoading(true)
    await exportExcelVehicleOwnershipReportByBrand(
      "Bảng thống kê chi tiết số lượng và tỷ lệ khách hàng theo số xe sở hữu", dataFilter
    )
    pushLoading(false)
  }, 1000)

  useEffect(() => {
    gSLProvice()
  }, [searchFilter?.searchProvince])

  useEffect(() => {
    gSLProfession()
  }, [searchFilter?.searchJob])

  useEffect(() => {
    gSLGroup()
  }, [searchFilter?.searchGroup])

  useEffect(() => {
    resetAllLoading()
    resetAllLoaded()
  }, [dataFilter, unitTemp])

  //#region useEffects gọi API khi vào viewport
  // giới tính & phân loại khách hàng
  useEffect(() => {
    //customerByInformationChannel
    const isVisible = visibleMap.customerReportGenderBussiness;
    const isLoaded = loadedMap.customerReportGenderBussiness;

    if (!isVisible || isLoaded) return;
    gDataCustomerReportGenderBussiness();
  }, [
    visibleMap.customerReportGenderBussiness,
    loadedMap.customerReportGenderBussiness,
  ]);

  // Biểu đồ  tỉ lệ xe sở hữu và xe sử dụng
  useEffect(() => {
    const isVisible = visibleMap.customerReportCarOwnerUsed;
    const isLoaded = loadedMap.customerReportCarOwnerUsed;

    if (!isVisible || isLoaded) return;
    gDataCustomerReportCarOwnerUsed();
  }, [
    visibleMap.customerReportCarOwnerUsed,
    loadedMap.customerReportCarOwnerUsed,
  ]);

  // xe theo tuổi
  useEffect(() => {
    const isVisible = visibleMap.customerReportAgeCar;
    const isLoaded = loadedMap.customerReportAgeCar;

    if (!isVisible || isLoaded) return;
    gDataCustomerReportAgeCar();
  }, [
    visibleMap.customerReportAgeCar,
    loadedMap.customerReportAgeCar,
  ]);

  // kh theo nghề nghiệp
  useEffect(() => {
    const isVisible = visibleMap.customerReportJob;
    const isLoaded = loadedMap.customerReportJob;

    if (!isVisible || isLoaded) return;
    gDataCustomerReportJob();
  }, [
    visibleMap.customerReportJob,
    loadedMap.customerReportJob,
  ]);

  // kh theo tỉnh thành
  useEffect(() => {
    const isVisible = visibleMap.customerReportAddress;
    const isLoaded = loadedMap.customerReportAddress;

    if (!isVisible || isLoaded) return;
    gDataCustomerReportAddress();
  }, [
    visibleMap.customerReportAddress,
    loadedMap.customerReportAddress,
  ]);


  // Chi tiết báo cáo khách hàng theo thương hiệu xe được sở hữu nhiều nhất
  useEffect(() => {
    const isVisible = visibleMap.customerReportCarOwnedBrand;
    const isLoaded = loadedMap.customerReportCarOwnedBrand;

    if (!isVisible || isLoaded) return;
    gDataCustomerReportCarOwnedBrand();
  }, [
    visibleMap.customerReportCarOwnedBrand,
    loadedMap.customerReportCarOwnedBrand,
  ]);

  // Thông tin báo cáo thương hiệu được sở hữu nhiều nhất theo đơn vị bán xe
  useEffect(() => {
    const isVisible = visibleMap.customerReportCarOwnedBrandByUnit;
    const isLoaded = loadedMap.customerReportCarOwnedBrandByUnit;

    if (!isVisible || isLoaded) return;
    gDataCustomerReportCarOwnedBrandByUnit();
  }, [
    visibleMap.customerReportCarOwnedBrandByUnit,
    loadedMap.customerReportCarOwnedBrandByUnit,
  ]);

  // Top 10 khách hàng theo kênh tiếp xúc/ nguồn
  useEffect(() => {
    //customerByInformationChannel
    const isVisible = visibleMap.customerByInformationChannel;
    const isLoaded = loadedMap.customerByInformationChannel;

    if (!isVisible || isLoaded) return;
    gCustomerByInformationChannel();
  }, [
    visibleMap.customerByInformationChannel,
    loadedMap.customerByInformationChannel,
  ]);

  // useEffect(() => {
  //   const isVisible = visibleMap.customerByInformationChannel;
  //   if (!isVisible) return;
  //   gCustomerByInformationChannel();
  // }, [valueTop[TYPEFILTERTOP.CUSTOMERBYINFORMATIONCHANNEL]])


  // Biểu đồ phân bố khách hàng theo thương hiệu và số lượng xe sở hữu
  useEffect(() => {
    //vehicleOwnershipReportByBrand
    const isVisible = visibleMap.vehicleOwnershipReportByBrand;
    const isLoaded = loadedMap.vehicleOwnershipReportByBrand;

    if (!isVisible || isLoaded) return;
    gVehicleOwnershipReportByBrand();
  }, [
    visibleMap.vehicleOwnershipReportByBrand,
    // loadedMap.vehicleOwnershipReportByBrand,
  ]);

  useEffect(() => {
    const isVisible = visibleMap.vehicleOwnershipReportByBrand;
    if (!isVisible) return;

    gVehicleOwnershipReportByBrand();
  }, [valueTop[TYPEFILTERTOP.VEHICLEOWNERSHIPREPORTBYBRAND]])

  // Biểu đồ phân bố khách hàng số lượng xe sở hữu
  useEffect(() => {
    //vehicleOwnershipReportByBrand
    const isVisible = visibleMap.vehicleOwnershipReport;
    const isLoaded = loadedMap.vehicleOwnershipReport;

    if (!isVisible || isLoaded) return;
    gVehicleOwnershipReport();
  }, [
    visibleMap.vehicleOwnershipReport,
    // loadedMap.vehicleOwnershipReportByBrand,
  ]);


  // Biểu đồ phân bố khách hàng theo thương hiệu và số lượng xe sở hữu Bảng
  useEffect(() => {
    //vehicleOwnershipReportByBrand
    const isVisible = visibleMap.vehicleOwnershipReportByBrandTable;
    const isLoaded = loadedMap.vehicleOwnershipReportByBrandTable;

    if (!isVisible || isLoaded) return;
    gListVehicleOwnershipReportByBrand();
  }, [
    visibleMap.vehicleOwnershipReportByBrandTable,
  ]);

  //#endregion

  // Gọi API cho tab "Thông tin xe" khi switch sang (vì Tabs lazy-render)
  useEffect(() => {
    if (activeTab !== '2') return;
    if (!loadedMap.customerReportCarOwnedBrand) gDataCustomerReportCarOwnedBrand();
    if (!loadedMap.customerReportCarOwnedBrandByUnit) gDataCustomerReportCarOwnedBrandByUnit();
    if (!loadedMap.vehicleOwnershipReport) gVehicleOwnershipReport();
    if (!loadedMap.vehicleOwnershipReportByBrand) gVehicleOwnershipReportByBrand();
    if (!loadedMap.vehicleOwnershipReportByBrandTable) gListVehicleOwnershipReportByBrand();
  }, [activeTab]);


  useEffect(() => {
    gSLUnitByPermission()
    gSLProfession()
    gSLProvice()
    gSLCarCompany()
    setDataFilterTmp(dataDefaulFilter)
    setDataFilter(dataDefaulFilter)

  }, [unitTemp])


  return (
    <Layout contentClassName='arc-content' className='custom-sidebar-chart'>

      <BreadcrumbBlock
        items={[
          { title: <Link to="/">THACO AUTO CRM</Link>, },
          { title: 'Báo cáo phân tích', },
          { title: 'Tổng quan khách hàng', },
        ]}
        title='Tổng quan khách hàng'
      />
      <div className="custom-page-css flex flex-col gap-3 w-full">

        <div className="custom-page-css flex items-center gap-3">

          <div className="custom-page-css flex flex-1 overflow-hidden">
            <TreeSelect
              className='custom-page-css border rounded overflow-hidden w-full custom-scroll-multi-select'
              multiple
              showSearch
              placeholder="Đơn vị"
              allowClear
              value={dataFilterTmp.unitId}
              treeDefaultExpandAll={false}
              treeData={sLUnitByPermission}
              filterTreeNode={(input: string, option: any) =>
                typeof option?.label === 'string' && option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              onChange={(e) => setDataFilterTmp({ ...dataFilterTmp, unitId: e !== undefined ? e : undefined })}
            />
          </div>
          <div className="custom-page-css flex flex-1 overflow-hidden">
            <TreeSelect
              className='custom-page-css border rounded overflow-hidden w-full custom-scroll-multi-select'
              multiple
              showSearch
              placeholder="Nhóm khách hàng"
              allowClear
              value={dataFilterTmp.group}
              treeDefaultExpandAll={false}
              treeData={listGroup}
              filterTreeNode={false}
              onSearch={(value) => setSearchFilter({ ...searchFilter, searchGroup: value })}
              onBlur={() => setSearchFilter({ ...searchFilter, searchGroup: '' })}
              onChange={(e) => setDataFilterTmp({ ...dataFilterTmp, group: e !== undefined ? e : undefined })}
              onClear={() => setDataFilterTmp({ ...dataFilterTmp, group: undefined })}
            />
          </div>
          <div className="custom-page-css flex flex-1 overflow-hidden">
            <Select
              className='custom-page-css border rounded overflow-hidden w-full custom-scroll-multi-select'
              placeholder="Loại khách hàng"
              mode='multiple'
              allowClear
              options={dataTypeCustomer}
              onChange={(e) => setDataFilterTmp({ ...dataFilterTmp, businessCheck: e })}
              onClear={() => setDataFilterTmp({ ...dataFilterTmp, businessCheck: undefined })}
            />
          </div>
          <div className="custom-page-css flex flex-1 overflow-hidden">

            <Select
              className='custom-page-css border rounded overflow-hidden  w-full custom-scroll-multi-select'
              mode='multiple'
              placeholder="Giới tính"
              allowClear
              options={dataGender}
              onChange={(e) => setDataFilterTmp({ ...dataFilterTmp, gender: e })}
              onClear={() => setDataFilterTmp({ ...dataFilterTmp, gender: undefined })}
            />
          </div>
          <Button
            className="custom-pape-css text-sm text-344054 font-regular px-3 py-1 border rounded"
            onClick={
              () => setDataFilterTmp({
                ...dataFilterTmp,
                isMore: !dataFilterTmp.isMore
              })
            }
          >
            {dataFilterTmp.isMore ? "Thu gọn" : "Mở rộng"}
            {dataFilterTmp.isMore ? <UpOutlined size={12} /> : <DownOutlined size={12} />}
          </Button>
          <Button
            className={` ${getPermissionClass(download)} custom-pape-css text-sm text-344054 font-regular px-3 py-1 border rounded`}
            onClick={handleExportReportAll}
          >
            <DownloadOutlined />
            Xuất báo cáo
          </Button>


          <Button
            className="h-fit custom-pape-css text-sm text-white bg-096DD9 font-regular px-3 py-1 border rounded"
            onClick={() => setDataFilter(dataFilterTmp)}
          >
            <SearchOutlined />
            Áp dụng lọc
          </Button>
        </div>

        {
          dataFilterTmp.isMore && (
            <div className="custom-page-css flex items-center gap-3">

              <div className="custom-page-css flex flex-1 overflow-hidden">
                <Select
                  className='custom-page-css border rounded overflow-hidden w-full custom-scroll-multi-select'
                  placeholder="Nghề nghiệp"
                  mode='multiple'
                  allowClear
                  options={sLProfession}
                  filterOption={false}
                  onBlur={() => setSearchFilter({ ...searchFilter, searchJob: '' })}
                  onSearch={(value) => setSearchFilter({ ...searchFilter, searchJob: value })}
                  onChange={(e) => setDataFilterTmp({ ...dataFilterTmp, job: e })}
                  onClear={() => setDataFilterTmp({ ...dataFilterTmp, job: undefined })}
                />
              </div>

              <div className="custom-page-css flex flex-1 overflow-hidden">
                <Select
                  className='custom-page-css border rounded overflow-hidden  w-full custom-scroll-multi-select'
                  mode='multiple'
                  placeholder="Tỉnh/ thành phố"
                  allowClear
                  options={sLProvice}
                  filterOption={false}
                  onBlur={() => setSearchFilter({ ...searchFilter, searchProvince: '' })}
                  onSearch={(value) => setSearchFilter({ ...searchFilter, searchProvince: value })}
                  onChange={(e) => setDataFilterTmp({ ...dataFilterTmp, province: e })}
                  onClear={() => setDataFilterTmp({ ...dataFilterTmp, province: undefined })}
                />
              </div>
              <div className="custom-page-css flex flex-1 overflow-hidden">
                <Select
                  className='custom-page-css border rounded overflow-hidden w-full custom-scroll-multi-select'
                  placeholder="Thương hiệu"
                  mode='multiple'
                  allowClear
                  options={sLCategoryCar}
                  onChange={(e) => setDataFilterTmp({ ...dataFilterTmp, carBrand: e })}
                  onClear={() => setDataFilterTmp({ ...dataFilterTmp, carBrand: undefined })}
                />
              </div>
              <div className="custom-page-css flex flex-1 py-2">
                <Popover
                  trigger="click"
                  placement="bottomLeft"
                  content={
                    <Space direction="vertical">
                      {
                        dataSelectAge.map(((item, i) => (
                          <Checkbox
                            key={i}
                            checked={dataFilterTmp.age?.includes(item.value)}
                            onChange={e =>
                              handleAgeChange(item.value, e.target.checked)
                            }
                          >
                            {item.label}
                          </Checkbox>
                        )))
                      }
                    </Space>
                  }
                >
                  <div
                    className='custom-page-css flex items-center gap-4 text-98A2B3 rounded border px-3 h-full cursor-pointer w-fit justify-center '
                    style={{
                      padding: "4px 12px",
                      fontSize: "14px",
                    }}
                    onClick={(e) => e.preventDefault()}
                  >
                    Độ tuổi <DownOutlined />
                  </div>
                </Popover>
              </div>

            </div>
          )
        }

      </div >

      <div className="custom-page-css  w-full flex gap-4">
        <Tabs
          className='h-full w-full custom-tab-child'
          activeKey={activeTab}
          onChange={setActiveTab}
          centered={false}
          items={[
            {
              key: '1',
              label: 'Nhân khẩu học',
              children:
                <>
                  <div className="custom-page-css flex items-stretch gap-4">
                    <div
                      ref={setRef("customerReportGenderBussiness")}
                      className="custom-page-css flex flex-1 "
                    >
                      <ChartCard title="Biểu đồ tỉ lệ giới tính" >
                        <GenderDonut
                          dataFilter={dataFilter}
                          data={dataCustomerReportGender}
                          showLoading={loadedMap.customerReportGenderBussiness}
                        />
                      </ChartCard>
                    </div>
                    <div
                      ref={setRef("customerReportGenderBussiness")}
                      className="custom-page-css flex flex-1 "
                    >
                      <ChartCard title="Biểu đồ tỉ lệ phân loại khách hàng" >
                        <CustomerTypePie
                          link={true}
                          dataFilter={dataFilter}
                          customerTypeData={dataCustomerType}
                          showLoading={loadedMap.customerReportGenderBussiness}
                        />
                      </ChartCard>
                    </div>
                    <div
                      ref={setRef("customerReportCarOwnerUsed")}
                      className="custom-page-css flex flex-1 ">
                      <ChartCard title="Biểu đồ tỉ lệ xe sở hữu, sử dụng và quan tâm" >
                        <CustomerTypePie
                          link={false}
                          dataFilter={dataFilter}

                          customerTypeData={dataOwnership}
                          showLoading={loadedMap.customerReportCarOwnerUsed}
                        />
                      </ChartCard>
                    </div>
                  </div>

                  <div
                    className="custom-page-css flex gap-4 pt-4"
                  >

                  </div>
                  <div
                    ref={setRef("customerReportAgeCar")}
                    className="custom-page-css flex flex-1 ">
                    <AgeDistributionChart
                      dataFilter={dataFilter}
                      data={dataAgeDistribution}
                      showLoading={loadedMap.customerReportAgeCar}
                    />
                  </div>
                  <div
                    className="custom-page-css flex gap-4 pt-4"
                  >
                    <div
                      ref={setRef("customerReportJob")}
                      className="custom-page-css flex flex-1 ">
                      <HorizontalBarChart
                        dataFilter={dataFilter}
                        link={true}

                        type={TYPEFILTERTOP.JOB}
                        title="Top khách hàng theo nghề nghiệp"
                        data={dataFormatCustomerJobs ?? topByJobs}
                        color="#5AA9FF"
                        valueTop={valueTop}
                        tooltipValue={'Các nghề nghiệp không thuộc Top sẽ được tổng hợp vào nhóm "Khác".'}
                        setValueTop={setValueTop}
                        showLoading={loadedMap.customerReportJob}
                      />
                    </div>
                    <div
                      ref={setRef("customerReportAddress")}
                      className="custom-page-css flex flex-1 ">
                      <HorizontalBarChart
                        type={TYPEFILTERTOP.REGION}
                        link={true}

                        dataFilter={dataFilter}
                        title="Top khách hàng theo khu vực"
                        data={dataFormatCustomerAddress ?? topByRegion}
                        color="#9B8CFF"
                        valueTop={valueTop}
                        tooltipValue={'Các khu vực không thuộc Top sẽ được tổng hợp vào nhóm "Khác".'}
                        setValueTop={setValueTop}
                        showLoading={loadedMap.customerReportAddress}
                      />
                    </div>
                  </div>
                  <div className="custom-page-css flex gap-4 pt-4">
                    <div
                      ref={setRef("customerByInformationChannel")}
                      className="custom-page-css flex flex-1">
                      <HorizontalBarChart
                        dataFilter={dataFilter}
                        type={TYPEFILTERTOP.CUSTOMERBYINFORMATIONCHANNEL}
                        title="Top khách hàng theo kênh tiếp xúc/ nguồn"
                        tooltipValue={'Các kênh tiếp xúc/ nguồn không thuộc Top sẽ được tổng hợp vào nhóm "Khác".'}
                        data={dataFormatCustomerByInformationChannel}
                        color="#8979FF"
                        valueTop={valueTop}
                        setValueTop={setValueTop}
                        showLoading={loadedMap.customerByInformationChannel}
                      />
                    </div>
                  </div>
                </>,
            },
            {
              key: '2',
              label: 'Thông tin xe',
              children:
                <div className='custom-page-css flex flex-col gap-4'>
                  <div className="custom-page-css flex gap-4">
                    <div
                      ref={setRef("customerReportCarOwnedBrand")}
                      className="custom-page-css flex flex-1"
                    >

                      <HorizontalBarChart
                        type={TYPEFILTERTOP.BRAND}
                        dataFilter={dataFilter}
                        tooltipValue={'Các thương hiệu không thuộc Top sẽ được tổng hợp vào nhóm "Khác".'}
                        title="Top thương hiệu được sở hữu nhiều nhất"
                        data={dataFormatTopOwnedBrand}
                        color="#5AA9FF"
                        valueTop={valueTop}
                        setValueTop={setValueTop}
                        showLoading={loadedMap.customerReportCarOwnedBrand}
                      />

                    </div>
                  </div>
                  <div
                    ref={setRef("customerReportCarOwnedBrandByUnit")}
                    className="custom-page-css flex flex-col flex-1 border rounded-2 gap-2 p-6">
                    <div className="custom-page-css flex justify-between items-center mb-4">

                      <div className="custom-pape-css flex gap-1 items-center ">
                        <span className="custom-page-css text-base text-344054 font-medium ">Biểu đồ số lượng xe sở hữu theo thương hiệu và đơn vị bán xe</span>

                        <TooltipComponent value={'Các đơn vị bán không thuộc Top sẽ được tổng hợp vào nhóm "Khác".'} />
                      </div>
                      <Select
                        size="small"
                        bordered={false}
                        className="custom-page-css  border rounded px-1 py-2 text-344054"
                        value={valueTop[TYPEFILTERTOP.BRANDSANDDEALERSHIPS]}
                        options={dataSelectTopReport}
                        onSelect={(value: number) =>
                          setValueTop(prev => ({
                            ...prev,
                            [TYPEFILTERTOP.BRANDSANDDEALERSHIPS]: value,
                          }))
                        }

                      />
                    </div>
                    <VehicleOwnershipChart
                      data={dataFormatCarOwnedBrandByUnit}
                      showLoading={loadedMap.customerReportCarOwnedBrandByUnit}
                      brandsCarOwnedBrandByUnit={brandsCarOwnedBrandByUnit}
                    />
                  </div>
                  {/* Biểu đồ phân bố khách hàng theo số lượng xe sở hữu */}
                  <div
                    ref={setRef("vehicleOwnershipReport")}
                    className="custom-page-css flex flex-col flex-1 border rounded-2 gap-2 p-6"
                    style={{ border: '1px solid #eaebec' }}
                  >
                    <div className="custom-page-css flex justify-between items-center mb-2">
                      <h3 className="custom-page-css text-344054 text-base font-medium">Biểu đồ phân bố khách hàng theo số lượng xe sở hữu</h3>
                    </div>
                    <div style={{ width: '100%', height: 420 }}>
                      {loadedMap.vehicleOwnershipReport ? (
                        <ResponsiveContainer>
                          <BarChart
                            data={dataVehicleOwnershipReportChart}
                            margin={{ top: 8, right: 20, left: 20, bottom: 8 }}
                          >
                            <defs>
                              <linearGradient id="ownershipGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#1890FF" stopOpacity={0.9} />
                                <stop offset="95%" stopColor="#9DD0FF" stopOpacity={0.7} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis tickFormatter={(value: number) => formatPrice(value)} />
                            {dataVehicleOwnershipReportChart.length > 0 && (
                              <RTooltip
                                formatter={(value: number) => [formatPrice(value), 'Tổng số khách hàng']}
                              />
                            )}
                            <Bar dataKey="value" fill="url(#ownershipGradient)" radius={[4, 4, 0, 0]} barSize={60} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="custom-page-css flex flex-col gap-2 items-center justify-center" style={{ width: '100%', height: '100%' }}>
                          <Spin size="default" />
                          <span>Đang tải...</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    ref={setRef("vehicleOwnershipReportByBrandTable")}
                  >
                    <ReportMarketingCampaign
                      data={dataVehicleOwnershipReportByBrandTable}
                      dataFilter={dataFilter}
                      showLoading={loadedMap.vehicleOwnershipReportByBrandTable}
                      handleExport={handleExcelVehicleOwnershipReportByBrand}
                    />
                  </div>
                </div>,
            },
          ]}
        />
      </div>
    </Layout >
  );
}

export default ReportCustomer;
